import { useContext } from 'react';
import Compare from '../Compare';
import Stack from '../../../shared/layouts/Stack';
import CategoryHeader from '../CategoryHeader';
import { CompareContext } from '../ComparePage';
import { cloneDeep } from 'lodash';
import { findTranslation } from '../../../../utils/helpers';
import { number } from 'prop-types';
import {useTranslation} from "react-i18next";

const SECTION = 'mortgage_loanpart';

const CompareLoanpartSection = ({ mortgageIndex }) => {
  const {
    showOnlyDiff,
    hasDiff,
    shouldShow,
    ownMortgageData,
    otherMortgageData,
    handleMortgageDataChange,
    loanpartFields,
    hasSectionCopiedFields,
    meta: { repaymentTypes = [] },
  } = useContext(CompareContext);

  const { t } = useTranslation();

  const handleLoanpartDataChange =
    (index, section, fieldName) =>
    ({ target: { name, value } }) => {
      const loanparts = cloneDeep(
        ownMortgageData[mortgageIndex]?.loanparts || []
      );

      loanparts[index] = {
        ...loanparts[index],
        [name]: value,
      };

      handleMortgageDataChange(
        {
          target: {
            name: 'loanparts',
            value: loanparts,
          },
        },
        mortgageIndex,
        section,
        fieldName
      );
    };

  const ownLoanparts = ownMortgageData?.[mortgageIndex]?.loanparts || [];
  const otherLoanparts = otherMortgageData?.[mortgageIndex]?.loanparts || [];
  const renderLoanparts =
    ownLoanparts?.length > otherLoanparts?.length
      ? ownLoanparts
      : otherLoanparts;

  return (
    renderLoanparts?.map((lp, index) => {
      const ownLoanpart = ownLoanparts?.[index] || {};
      const otherLoanpart = otherLoanparts?.[index] || {};
      const SECTION_LOANPART = `${SECTION}_${mortgageIndex}${index}`;

      if (
        showOnlyDiff &&
        !hasSectionCopiedFields(SECTION_LOANPART) &&
        !hasDiff(loanpartFields, ownLoanpart, otherLoanpart)
      )
        return null;

      return (
        <Stack key={`loanpart_${mortgageIndex}.${index}`} space="l">
          <CategoryHeader>
            <span>{t('Leningdeel {{index}}', {index: index + 1})}</span>
            <span>{t('Leningdeel {{index}}', {index: index + 1})}</span>
          </CategoryHeader>
          <Stack space="m">
            {shouldShow(
              'loanparttype',
              ownLoanpart,
              otherLoanpart,
              SECTION_LOANPART
            ) && (
              <Compare
                label={t("Aflosvorm")}
                name="loanparttype"
                section={SECTION_LOANPART}
                leftValue={ownLoanpart?.loanparttype}
                rightValue={otherLoanpart?.loanparttype}
                valueTransformer={(value) =>
                  findTranslation(value, repaymentTypes)
                }
                onUpdate={handleLoanpartDataChange(
                  index,
                  SECTION_LOANPART,
                  'loanparttype'
                )}
              />
            )}
            {shouldShow(
              'loanpartnumber',
              ownLoanpart,
              otherLoanpart,
              SECTION_LOANPART
            ) && (
              <Compare
                label={t("Leningdeelnummer")}
                name="loanpartnumber"
                section={SECTION_LOANPART}
                leftValue={ownLoanpart?.loanpartnumber}
                rightValue={otherLoanpart?.loanpartnumber}
                onUpdate={handleLoanpartDataChange(
                  index,
                  SECTION_LOANPART,
                  'loanpartnumber'
                )}
              />
            )}
            {shouldShow(
              'nhg',
              ownLoanpart,
              otherLoanpart,
              SECTION_LOANPART
            ) && (
              <Compare
                label={t("NHG")}
                name="nhg"
                section={SECTION_LOANPART}
                leftValue={ownLoanpart?.nhg}
                rightValue={otherLoanpart?.nhg}
                onUpdate={handleLoanpartDataChange(
                  index,
                  SECTION_LOANPART,
                  'nhg'
                )}
              />
            )}
            {shouldShow(
              'originalAmount',
              ownLoanpart,
              otherLoanpart,
              SECTION_LOANPART
            ) && (
              <Compare
                label={t("Oorspronkelijke schuld")}
                name="originalAmount"
                section={SECTION_LOANPART}
                leftValue={ownLoanpart?.originalAmount}
                rightValue={otherLoanpart?.originalAmount}
                onUpdate={handleLoanpartDataChange(
                  index,
                  SECTION_LOANPART,
                  'originalAmount'
                )}
                isCurrency
              />
            )}
            {shouldShow(
              'currentAmount',
              ownLoanpart,
              otherLoanpart,
              SECTION_LOANPART
            ) && (
              <Compare
                label={t("Resterende schuld")}
                name="currentAmount"
                section={SECTION_LOANPART}
                leftValue={ownLoanpart?.currentAmount}
                rightValue={otherLoanpart?.currentAmount}
                onUpdate={handleLoanpartDataChange(
                  index,
                  SECTION_LOANPART,
                  'currentAmount'
                )}
                isCurrency
              />
            )}
            {shouldShow(
              'startDate',
              ownLoanpart,
              otherLoanpart,
              SECTION_LOANPART
            ) && (
              <Compare
                label={t("Ingangsdatum leningdeel")}
                name="startDate"
                section={SECTION_LOANPART}
                leftValue={ownLoanpart?.startDate}
                rightValue={otherLoanpart?.startDate}
                valueTransformer={(value) =>
                  value ? value.split('-') : ['', '', '']
                }
                onUpdate={handleLoanpartDataChange(
                  index,
                  SECTION_LOANPART,
                  'startDate'
                )}
              />
            )}
            {shouldShow(
              'duration',
              ownLoanpart,
              otherLoanpart,
              SECTION_LOANPART
            ) && (
              <Compare
                label={t("Looptijd")}
                name="duration"
                section={SECTION_LOANPART}
                valueTransformer={(value) =>
                  value
                    ? [
                      Math.trunc(value / 12) + ' jaar',
                      (value % 12) + ' maanden',
                      ]
                    : ['', '']
                }
                leftValue={ownLoanpart?.duration}
                rightValue={otherLoanpart?.duration}
                onUpdate={handleLoanpartDataChange(
                  index,
                  SECTION_LOANPART,
                  'duration'
                )}
              />
            )}
            {shouldShow(
              'frtStartDate',
              ownLoanpart,
              otherLoanpart,
              SECTION_LOANPART
            ) && (
              <Compare
                label={t("Ingangsdatum rentevast periode")}
                name="frtStartDate"
                section={SECTION_LOANPART}
                leftValue={ownLoanpart?.frtStartDate}
                rightValue={otherLoanpart?.frtStartDate}
                valueTransformer={(value) =>
                  value ? value.split('-') : ['', '', '']
                }
                onUpdate={handleLoanpartDataChange(
                  index,
                  SECTION_LOANPART,
                  'frtStartDate'
                )}
              />
            )}
            {shouldShow(
              'frtDuration',
              ownLoanpart,
              otherLoanpart,
              SECTION_LOANPART
            ) && (
              <Compare
                label={t("Periode")}
                name="frtDuration"
                section={SECTION_LOANPART}
                leftValue={ownLoanpart?.frtDuration}
                rightValue={otherLoanpart?.frtDuration}
                valueTransformer={(value) =>
                  value ? Math.floor(value / 12) + ' jaar rentevast' : null
                }
                onUpdate={handleLoanpartDataChange(
                  index,
                  SECTION_LOANPART,
                  'frtDuration'
                )}
              />
            )}
            {shouldShow(
              'interest',
              ownLoanpart,
              otherLoanpart,
              SECTION_LOANPART
            ) && (
              <Compare
                label={t("Rente")}
                name="interest"
                section={SECTION_LOANPART}
                leftValue={ownLoanpart?.interest}
                rightValue={otherLoanpart?.interest}
                onUpdate={handleLoanpartDataChange(
                  index,
                  SECTION_LOANPART,
                  'interest'
                )}
                isPercentage
              />
            )}
            {shouldShow(
              'ltv',
              ownLoanpart,
              otherLoanpart,
              SECTION_LOANPART
            ) && (
              <Compare
                label={t("Oorspronkelijke tariefklasse")}
                name="ltv"
                section={SECTION_LOANPART}
                leftValue={ownLoanpart?.ltv}
                rightValue={otherLoanpart?.ltv}
                onUpdate={handleLoanpartDataChange(
                  index,
                  SECTION_LOANPART,
                  'ltv'
                )}
                isPercentage
              />
            )}
          </Stack>
        </Stack>
      );
    }) || null
  );
};

CompareLoanpartSection.propTypes = {
  mortgageIndex: number.isRequired,
};

export default CompareLoanpartSection;
